import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import TipContext from '@home/components/TipContext';
import FinanceCard from './components/FinanceCard';
import FinanceTableAndEcharts from './components/FinanceTableAndEcharts';
import DevBudgetChangeCard from './components/DevBudgetChangeCard';
import usePageLog from '@gp/hooks/usePageLog';
import useAutoScroll2TargetModule from '../_hooks/useAutoScroll2TargetModule';
// import { useGetFetchKeyMetricsInfo } from './hooks';
import { getKeyMetricsTitleTips } from './utils/tips';
import { sortMetrics } from './utils/dataTransfer';
import { fetchFinanceKeyMetricsV2 } from '@gp/services/finance';
import type { IBaseProps } from '@home/types/common';
import type { IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';

import './index.scss';

interface IFinanceProps extends IBaseProps {
  pageContainer: React.RefObject<HTMLElement>;
  projectDetailData?: {
    project_id?: string;
    name?: string;
    studio_entity_id?: string;
  };
  currentMilestone?: IMilestone | null;
}

const METRIC_TITLES: Record<string, string> = {
  y3_sales: 'Y3 Revenue Forecast ($)',
  y3_copies: 'Y3 Copy Sales Forecast',
  y1_sales: 'Y1 Revenue Forecast ($)',
  y1_copies: 'Y1 Copy Sales Forecast',
};

const Finance: React.FC<IFinanceProps> = (props) => {
  const { style, className, pageContainer, projectDetailData, currentMilestone } = props;
  const { project_id: projectId, studio_entity_id: studioId, name } = projectDetailData || {};

  const [metricsData, setMetricsData] = useState<any>({});
  const [metricsDataLoading, setMetricsDataLoading] = useState(false);

  // const { loading: metricsDataLoading, data: metricsData } = useGetFetchKeyMetricsInfo(
  //   useMemo<{ project_id: string }>(() => ({ project_id: projectId || '' }), [projectId]),
  // );

  const getFetchKeyMetricsInfo = async () => {
    try {
      setMetricsDataLoading(true);
      const res = await fetchFinanceKeyMetricsV2({ project_id: projectId || '' });
      setMetricsData(res?.data || {});
    } catch (exception) {
      console.error(exception);
    } finally {
      setMetricsDataLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      getFetchKeyMetricsInfo();
    }
  }, [projectId]);

  const metricsDataLatestDataTime = useMemo<string>(
    () => (metricsData as any)?.update_time,
    [metricsData],
  );
  // 排个序，固定展示
  const metricsDataRangeCard = useMemo<any[]>(
    () => sortMetrics((metricsData as any)?.range_card),
    [metricsData],
  );

  // metricsList数据
  const metricsList = useMemo<Record<string, [any, string]>>(() => {
    const { spend_to_date: spendToDate, current_headcount: currentHeadcount } =
      (metricsData as any) || {};
    return {
      1: [spendToDate, 'Spend to Date ($)'],
      2: [currentHeadcount, 'Current Headcount'],
      3: [metricsDataRangeCard?.[0], 'Net Profit Ratio'],
    };
  }, [metricsData, metricsDataRangeCard]);

  useAutoScroll2TargetModule({
    pageContainer,
    loading: metricsDataLoading,
  });

  // 页面打点
  usePageLog({
    pageId: 'PIPELINE_DETAIL_FINANCE',
    uidType: 'game_code',
    uid: projectId || '',
    extInfo: name || '',
  });

  return (
    <div style={style} className={classnames('dbg-finance-detail', className)}>
      <div className="dbg-finance-detail__key-metrics">
        <div className="dbg-finance-detail-title">
          Key Metrics
          <TipContext
            className="dbg-finance-detail-icon"
            overlayClassName="dbg-bubble-max-width"
            placement="right"
            tipText={getKeyMetricsTitleTips(metricsDataLatestDataTime)}
          />
        </div>
        <div className="dbg-finance-detail-row">
          {Object.entries(metricsList).map(([key, val]) => {
            const [data, title] = val || [];
            return (
              <div key={key} className="dbg-finance-detail-col-3">
                <FinanceCard type={key} title={title} loading={metricsDataLoading} data={data} />
              </div>
            );
          })}
        </div>
        <div className="dbg-finance-detail-row">
          {metricsDataRangeCard?.slice(1)?.map((item, index) => (
            <div className="dbg-finance-detail-col-4" key={index}>
              <FinanceCard
                type="3"
                title={METRIC_TITLES?.[item?.metric]}
                loading={metricsDataLoading}
                data={item}
              />
            </div>
          ))}
        </div>
      </div>
      {/* Trends模块 表格、折线图、柱状图 */}
      <FinanceTableAndEcharts projectId={projectId} studioId={studioId} />
      {/* Development Budget Change模块 */}
      <DevBudgetChangeCard projectId={projectId} currentMilestone={currentMilestone} />
    </div>
  );
};

export default Finance;
